/**
 * SHOP ITEM CARD - Compact Game-Style Design
 * Displays shop items in a compact, visually appealing grid
 */

import { useState } from 'react';
import type { ShopItem } from '@/config/tisa-settings';
import { Coins, Sparkles, Lock, Zap } from 'lucide-react';

interface ShopItemCardProps {
  item: ShopItem;
  vaultBalance: number;
  studentLevel: number;
  onPurchase: (item: ShopItem) => void;
}

// Rarity color themes - compact version
const rarityStyles = {
  common: {
    border: 'border-l-4 border-gray-400',
    bg: 'bg-gradient-to-br from-white to-gray-50',
    glow: '',
    badge: 'bg-gray-500',
    priceColor: 'text-gray-700'
  },
  rare: {
    border: 'border-l-4 border-blue-500',
    bg: 'bg-gradient-to-br from-white to-blue-50',
    glow: 'shadow-[0_0_15px_rgba(59,130,246,0.4)]',
    badge: 'bg-blue-500',
    priceColor: 'text-blue-700'
  },
  epic: {
    border: 'border-l-4 border-purple-500',
    bg: 'bg-gradient-to-br from-white to-purple-50',
    glow: 'shadow-[0_0_20px_rgba(168,85,247,0.6)] animate-pulse-slow',
    badge: 'bg-purple-500',
    priceColor: 'text-purple-700'
  },
  legendary: {
    border: 'border-l-4 border-yellow-500',
    bg: 'bg-gradient-to-br from-white to-yellow-50',
    glow: 'shadow-[0_0_25px_rgba(251,191,36,0.8)] animate-pulse-gold',
    badge: 'bg-gradient-to-r from-yellow-500 to-orange-500',
    priceColor: 'text-yellow-700'
  }
};

export function ShopItemCard({ item, vaultBalance, studentLevel, onPurchase }: ShopItemCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const rarity = item.rarity || 'common';
  const styles = rarityStyles[rarity];

  const canAfford = vaultBalance >= item.price;
  const meetsLevel = !item.requiredLevel || studentLevel >= item.requiredLevel;
  const inStock = !item.stock || item.stock > 0;
  const canPurchase = canAfford && meetsLevel && inStock;

  return (
    <div
      className={`
        group relative rounded-lg overflow-hidden transition-all duration-200 cursor-pointer
        w-full h-[80px]
        ${styles.border} ${styles.bg}
        ${isHovered && canPurchase ? `${styles.glow} scale-105 z-10` : 'hover:shadow-md'}
        ${!canPurchase ? 'opacity-60 cursor-not-allowed' : ''}
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => canPurchase && onPurchase(item)}
    >
      {/* Top Section - Icon & Info */}
      <div className="relative p-2 flex items-start gap-2">
        {/* Item Icon - Smaller */}
        <div className="text-3xl flex-shrink-0">
          {item.icon}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          {/* Name */}
          <h3 className="font-bold text-xs leading-tight mb-1 line-clamp-2 group-hover:text-purple-600 transition">
            {item.name}
          </h3>

          {/* Price - Centered */}
          <div className="flex items-center justify-center gap-1">
            <Coins className={`w-3.5 h-3.5 ${canAfford ? 'text-yellow-500' : 'text-red-500'}`} />
            <span className={`font-bold text-base ${canAfford ? 'text-yellow-600' : 'text-red-500'}`}>
              {item.price}
            </span>
          </div>
        </div>

        {/* Rarity Badge - Top Right - Smaller */}
        <div className={`${styles.badge} w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0`}>
          <Sparkles className="w-2.5 h-2.5 text-white" />
        </div>
      </div>

      {/* Tags Row - More Compact */}
      <div className="px-2 pb-1.5 flex flex-wrap gap-1 text-[10px]">
        {/* Use Effect */}
        {item.useEffect && (
          <span className="bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
            <Zap className="w-2.5 h-2.5" />
            {item.useEffect.split('_')[0]}
          </span>
        )}

        {/* Level Requirement */}
        {item.requiredLevel && (
          <span className={`px-1.5 py-0.5 rounded-full ${meetsLevel ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
            {meetsLevel ? '✓' : '🔒'} Lv.{item.requiredLevel}
          </span>
        )}

        {/* Availability */}
        {item.availability && item.availability !== 'always' && (
          <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">
            {item.availability === 'weekly' ? '📅 Weekly' :
             item.availability === 'monthly' ? '📅 Monthly' :
             item.availability === 'limited' ? '⏰ Limited' :
             item.availability === 'mystery' ? '✨ Mystery' : ''}
          </span>
        )}

        {/* Stock */}
        {item.stock !== undefined && item.stock <= 5 && (
          <span className={`px-1.5 py-0.5 rounded-full ${item.stock > 0 ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'}`}>
            {item.stock > 0 ? `${item.stock} left` : 'Sold out'}
          </span>
        )}
      </div>

      {/* Hover Overlay - Quick Buy */}
      {canPurchase && isHovered && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex items-end p-2 animate-in fade-in duration-150">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPurchase(item);
            }}
            className="w-full py-1.5 px-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-xs font-bold rounded-lg shadow-lg transition-all transform hover:scale-105"
          >
            Buy Now
          </button>
        </div>
      )}

      {/* Locked Overlay - Compact */}
      {!canPurchase && (
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center backdrop-blur-[1px]">
          {/* Actual Price - Center - Height 42px */}
          <div className="text-center bg-white/95 rounded-lg px-3 py-1.5 shadow-lg h-[42px] flex flex-col items-center justify-center gap-0.5">
            {/* Price with lock on same line */}
            <div className="flex items-center justify-center gap-1.5">
              <Coins className="w-4 h-4 text-gray-600" />
              <span className="text-base font-bold text-gray-700">{item.price}</span>
              <Lock className="w-4 h-4 text-red-500" />
            </div>
            {/* Only show reason for level/stock, not coins */}
            {!meetsLevel && (
              <p className="text-[9px] font-semibold text-red-600">
                Level {item.requiredLevel} required
              </p>
            )}
            {!inStock && (
              <p className="text-[9px] font-semibold text-red-600">
                Out of stock
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
